import { Box, Button, Dialog, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { useResetIntegrationStatusesMutation } from "../../features/user/userApiSlice";

export const IntegrationDisconnectedDialog = ({ onClose, open, statuses }) => {
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();
	const [resetIntegrations, { isLoading: isResetLoading }] =
		useResetIntegrationStatusesMutation();

	const integrationsDisconnectedCount = Object.values(statuses).filter(
		(status) => status === 1
	).length;

	const handleResetIntegrations = async () => {
		await resetIntegrations()
			.unwrap()
			.then(() => {
				enqueueSnackbar(t("restarted_integration_status"), {
					variant: "success",
				});
			})
			.catch((error) => {
				if (error?.data?.errors) {
					const errors = error.data.errors;
					Object.entries(errors).forEach(([_key, message]) => {
						enqueueSnackbar(message[0], {
							variant: "error",
							preventDuplicate: true,
						});
					});
				} else {
					enqueueSnackbar(error?.data?.message || t("default_error"), {
						variant: "error",
						preventDuplicate: true,
					});
				}
			})
			.finally(() => {
				onClose();
			});
	};

	return (
		<Dialog fullWidth maxWidth="sm" onClose={onClose} open={open}>
			<Box sx={{ padding: "30px" }}>
				<Typography
					variant="h4"
					component="h2"
					sx={{ textAlign: "center", mb: "10px" }}
				>
					{integrationsDisconnectedCount > 1
						? t("disconnected_integrations")
						: t("disconnected_integration")}
				</Typography>
				{integrationsDisconnectedCount === 1 && statuses.trans === 1 && (
					<TransDisconnected />
				)}
				{integrationsDisconnectedCount === 1 && statuses.teleroute === 1 && (
					<TelerouteDisconnected />
				)}
				{integrationsDisconnectedCount === 1 && statuses.timocom === 1 && (
					<TimocomDisconnected />
				)}
				{integrationsDisconnectedCount > 1 && (
					<MultiDisconnected statuses={statuses} />
				)}
				<Box
					sx={{ display: "flex", justifyContent: "space-around", mt: "20px" }}
				>
					<Link to="/integrations">
						<Button
							variant="contained"
							onClick={onClose}
							disabled={isResetLoading}
						>
							{t("redirect_to_integration_settings")}
						</Button>
					</Link>
					<Button
						loading={isResetLoading}
						variant="contained"
						color="error"
						disabled={isResetLoading}
						onClick={handleResetIntegrations}
					>
						{t("ignore")}
					</Button>
				</Box>
			</Box>
		</Dialog>
	);
};

const TransDisconnected = () => {
	const { t } = useTranslation();

	return (
		<>
			<Typography
				variant="p"
				component="p"
				sx={{ textAlign: "center", mb: "10px" }}
			>
				{t("we_detected_that_your_integration_with_stock")}{" "}
				<a
					href="https://www.trans.eu/"
					target="_blank"
					rel="noopener noreferrer"
				>
					Trans.eu
				</a>{" "}
				{t("we_detected_is_disconnected")}
			</Typography>
			<Typography
				variant="p"
				component="p"
				sx={{ textAlign: "center", mb: "20px" }}
			>
				{t("urgently_update_your_integration")}
			</Typography>
		</>
	);
};

const TelerouteDisconnected = () => {
	const { t } = useTranslation();

	return (
		<>
			<Typography
				variant="p"
				component="p"
				sx={{ textAlign: "center", mb: "10px" }}
			>
				{t("we_detected_that_your_integration_with_stock")}{" "}
				<a
					href="https://teleroute.com/"
					target="_blank"
					rel="noopener noreferrer"
				>
					Teleroute
				</a>{" "}
				{t("we_detected_is_disconnected")}
			</Typography>
			<Typography
				variant="p"
				component="p"
				sx={{ textAlign: "center", mb: "20px" }}
			>
				{t("urgently_update_your_integration")}
			</Typography>
		</>
	);
};

const TimocomDisconnected = () => {
	const { t } = useTranslation();

	return (
		<>
			<Typography
				variant="p"
				component="p"
				sx={{ textAlign: "center", mb: "10px" }}
			>
				{t("we_detected_that_your_integration_with_stock")}{" "}
				<a
					href="https://www.timocom.pl/"
					target="_blank"
					rel="noopener noreferrer"
				>
					TIMOCOM
				</a>{" "}
				{t("we_detected_is_disconnected")}
			</Typography>
			<Typography
				variant="p"
				component="p"
				sx={{ textAlign: "center", mb: "20px" }}
			>
				{t("we_re_working_on_integration_reconnection")}
			</Typography>
		</>
	);
};

const MultiDisconnected = ({ statuses }) => {
	const { t } = useTranslation();

	return (
		<>
			<ul>
				{statuses.trans === 1 && (
					<li>
						<a
							href="https://www.trans.eu/"
							target="_blank"
							rel="noopener noreferrer"
						>
							Trans.eu
						</a>
					</li>
				)}
				{statuses.teleroute === 1 && (
					<li>
						<a
							href="https://teleroute.com/"
							target="_blank"
							rel="noopener noreferrer"
						>
							Teleroute
						</a>
					</li>
				)}
				{(statuses.trans === 1 || statuses.teleroute === 1) && (
					<Typography sx={{ my: "8px" }}>
						{t("urgently_update_your_integration")}
					</Typography>
				)}
				{statuses.timocom === 1 && (
					<>
						<li>
							<a
								href="https://www.timocom.pl/"
								target="_blank"
								rel="noopener noreferrer"
							>
								TIMOCOM
							</a>
						</li>
						<Typography sx={{ mt: "8px" }}>
							{t("we_re_working_on_integration_reconnection")}
						</Typography>
					</>
				)}
			</ul>
		</>
	);
};
